import * as Sentry from "@sentry/browser";
import Cookies from "js-cookie";
import React from "react";
import ReactDOM from "react-dom";
import { JssProvider } from "react-jss";

import { ApolloProvider } from "@apollo/client/react";
import { createBrowserHistory } from "history";
import { Redirect, Route, Router, Switch } from "react-router";
import { reverse, route } from "./urls";

import { createApolloClient } from "./backend";

import LoginScene from "./scenes/login/LoginScene";
import ResetPasswordScene from "./scenes/login/ResetPasswordScene";
import ResetSetPasswordScene from "./scenes/login/ResetSetPasswordScene";

import AccountListScene from "./scenes/app/AccountListScene";

import ErrorBoundary from "./components/ErrorBoundary";
import AccountDetailScene from "./scenes/app/AccountDetailScene";
import EvaluationFormSettingsAddScene from "./scenes/app/evaluationFormConfiguration/AddScene";
import EvaluationFormSettingsEditScene from "./scenes/app/evaluationFormConfiguration/EditScene";
import LicenseRenewalTemplatesEditScene from "./scenes/app/licenseRenewalTemplates/EditScene";
import LicenseAgreementAddScene from "./scenes/app/licenseType/AddAgreementScene";
import LicenseTypeAddScene from "./scenes/app/licenseType/AddScene";
import LicenseAgreementEditScene from "./scenes/app/licenseType/EditAgreementScene";
import LicenseRenewalPeriodEditScene from "./scenes/app/licenseType/EditRenewalPeriodScene";
import LicenseTypeEditScene from "./scenes/app/licenseType/EditScene";
import RegistrationFormSettingsAddScene from "./scenes/app/registrationFormConfiguration/AddScene";
import RegistrationFormSettingsEditScene from "./scenes/app/registrationFormConfiguration/EditScene";
import DevIndexScene from "./scenes/dev/DevIndexScene";
import DevSettingsScene from "./scenes/dev/DevSettingsScene";
import ActivateScene from "./scenes/login/ActivateScene";
import NotFoundScene from "./scenes/system/NotFoundScene";
import { backendUrl, getEnvironment, getHostEnvironment } from "./utils";

import DashboardScene from "./scenes/app/DashboardScene";
import SettingsScene from "./scenes/app/account/SettingsScene";
import AttendeeDetailScene from "./scenes/app/attendees/AttendeeDetailScene";
import BrandAttendeeListScene from "./scenes/app/attendees/BrandAttendeeListScene";
import BrandEventListScene from "./scenes/app/attendees/BrandEventListScene";
import KnowledgeModulesScene from "./scenes/app/brandSettings/LearningModulesScene";
import LicenseTypesScene from "./scenes/app/brandSettings/LicenseTypesScene";
import AddCompanyScene from "./scenes/app/company/AddCompanyScene";
import ContactDetailScene from "./scenes/app/contacts/ContactDetailScene";
import ContactListScene from "./scenes/app/contacts/ContactListScene";
import InvoiceListScene from "./scenes/app/invoices/InvoiceListScene";
import BrandAddCompanyScene from "./scenes/app/licensee/BrandAddCompanyScene";
import BrandAddManagerScene from "./scenes/app/licensee/BrandAddManagerScene";
import BrandEditCompanyScene from "./scenes/app/licensee/BrandEditCompanyScene";
import BrandEditManagerScene from "./scenes/app/licensee/BrandEditManagerScene";
import BrandLicenseeListScene from "./scenes/app/licensee/BrandLicenseeListScene";
import LicenseeProfileScene from "./scenes/app/licensee/LicenseeProfileScene";

((dsn, environment) => {
  if (!dsn) {
    return;
  }

  if (environment === "development") {
    return;
  }

  const debug = Cookies.get("debug") === "1";
  Sentry.init({ dsn, debug, environment });
})(process.env.SENTRY_DSN, getHostEnvironment());

function App() {
  const history = React.useMemo(() => createBrowserHistory(), []);

  const apolloClient = React.useMemo(
    () =>
      createApolloClient({
        // eslint-disable-next-line global-require
        introspection: require("./schema-fragment-matcher.json"),
        httpEndpoint: backendUrl("/graphql"),
        onError: (error: { networkError: { statusCode: number } }) => {
          if (error.networkError && error.networkError.statusCode === 401) {
            // Unauthorized
            apolloClient.clearStore();
            history.push(reverse("login"));
          }
        },
      }),
    [history]
  );

  return (
    <ErrorBoundary>
      <JssProvider
        id={{ minify: getEnvironment() === "production" }}
        classNamePrefix="wsb-"
      >
        <ApolloProvider client={apolloClient}>
          <Router history={history}>
            <Switch>
              <Route exact path="/">
                <Redirect to={reverse("dev:index")} />
              </Route>

              <Route
                path={route("attendees_detail")}
                component={AttendeeDetailScene}
              />
              <Route
                path={route("attendees_list")}
                component={BrandAttendeeListScene}
              />
              <Route
                path={route("events_list")}
                component={BrandEventListScene}
              />

              <Route
                path={route("brand_settings_license_types")}
                component={LicenseTypesScene}
              />
              <Route
                path={route("brand_settings_knowledge_modules")}
                component={KnowledgeModulesScene}
              />
              <Route
                path={route("licensee_managers_edit")}
                component={BrandEditManagerScene}
              />
              <Route
                path={route("licensee_managers_add")}
                component={BrandAddManagerScene}
              />
              <Route
                path={route("account_settings")}
                component={SettingsScene}
              />

              <Route
                path={route("licensees_add_company")}
                component={BrandAddCompanyScene}
              />
              <Route
                path={route("licensee_edit_company")}
                component={BrandEditCompanyScene}
              />
              <Route
                path={route("licensee_profile")}
                component={LicenseeProfileScene}
              />
              <Route
                path={route("licensees_list")}
                component={BrandLicenseeListScene}
              />

              <Route
                path={route("company_form_add")}
                component={AddCompanyScene}
              />

              <Route
                path={route("contacts_detail")}
                component={ContactDetailScene}
              />
              <Route
                exact
                path={route("contacts_list")}
                component={ContactListScene}
              />

              <Route
                exact
                path={route("invoices_list")}
                component={InvoiceListScene}
              />

              <Route
                exact
                path={route("dashboard")}
                component={DashboardScene}
              />

              <Route exact path={route("login")} component={LoginScene} />
              <Route
                exact
                path={route("reset-password")}
                component={ResetPasswordScene}
              />
              <Route
                exact
                path={route("reset-set-password")}
                component={ResetSetPasswordScene}
              />
              <Route exact path={route("activate")} component={ActivateScene} />

              <Route
                path={route("registration_form_configuration_edit")}
                component={RegistrationFormSettingsEditScene}
              />
              <Route
                path={route("registration_form_configuration_add")}
                component={RegistrationFormSettingsAddScene}
              />

              <Route
                path={route("evaluation_form_edit")}
                component={EvaluationFormSettingsEditScene}
              />
              <Route
                path={route("evaluation_form_add")}
                component={EvaluationFormSettingsAddScene}
              />

              <Route
                path={route("license_agreement_edit")}
                component={LicenseAgreementEditScene}
              />
              <Route
                path={route("license_agreement_add")}
                component={LicenseAgreementAddScene}
              />

              <Route
                path={route("license_type_add")}
                component={LicenseTypeAddScene}
              />
              <Route
                path={route("license_type_edit")}
                component={LicenseTypeEditScene}
              />

              <Route
                path={route("license_renewal_period_edit")}
                component={LicenseRenewalPeriodEditScene}
              />
              <Route
                path={route("license_renewal_templates_edit")}
                component={LicenseRenewalTemplatesEditScene}
              />

              <Route
                exact
                path={route("accounts_list")}
                component={AccountListScene}
              />
              <Route
                exact
                path={route("accounts_detail")}
                component={AccountDetailScene}
              />

              <Route
                exact
                path={route("dev:index")}
                component={DevIndexScene}
              />
              <Route
                exact
                path={route("dev:settings")}
                component={DevSettingsScene}
              />

              <Route component={NotFoundScene} />
            </Switch>
          </Router>
        </ApolloProvider>
      </JssProvider>
    </ErrorBoundary>
  );
}

ReactDOM.render(<App />, document.getElementById("react-wsb-root"));
